import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  Stack,
  Alert,
  Snackbar,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Divider,
  IconButton,
} from '@mui/material';
import { ProjectAutocomplete } from './ProjectAutocomplete';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Grid from '@mui/material/GridLegacy';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import {
  createFinanceAdjustment,
  createFinancePackage,
  deleteFinancePackage,
  getClients,
  getFinanceEntries,
  getFinancePackages,
  getFinanceSummary,
  getFinanceTimeseries,
  getProjects,
  updateFinancePackage,
} from '../api';
import {
  FinanceSummaryItem,
  FinanceTimeseriesPoint,
  LeadFinanceEntry,
  LeadFinancePackage,
  LeadPackagePaymentStatus,
} from '../types/finance';

import { ProjectExpensesTab } from './ProjectExpensesTab';

interface ProjectOption {
  id: number;
  name: string;
}

interface ClientOption {
  id: number;
  name: string;
}

interface PackageFormState {
  projectId: string;
  secondaryProjectIds: string[];
  clientId: string;
  title: string;
  leadQuota: string;
  unitPrice: string;
  totalAmount: string;
  paidAmount: string;
  paymentStatus: LeadPackagePaymentStatus;
  paymentDate: string;
  invoiceNumber: string;
  notes: string;
}

interface AdjustmentFormState {
  projectId: string;
  clientId: string;
  amount: string;
  leadCount: string;
  description: string;
  effectiveAt: string;
}

const PAYMENT_STATUS_LABELS: Record<LeadPackagePaymentStatus, string> = {
  PENDING: 'Ожидает оплаты',
  PARTIALLY_PAID: 'Частично оплачено',
  PAID: 'Оплачено',
  CANCELLED: 'Отменено',
  REFUNDED: 'Возврат',
};

const currencyFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat('ru-RU');

const toInputDate = (iso?: string | null) => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

const defaultPackageForm: PackageFormState = {
  projectId: '',
  secondaryProjectIds: [],
  clientId: '',
  title: '',
  leadQuota: '',
  unitPrice: '',
  totalAmount: '',
  paidAmount: '',
  paymentStatus: 'PENDING',
  paymentDate: '',
  invoiceNumber: '',
  notes: '',
};

const defaultAdjustmentForm: AdjustmentFormState = {
  projectId: '',
  clientId: '',
  amount: '',
  leadCount: '',
  description: '',
  effectiveAt: '',
};

interface PackageDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: PackageFormState) => Promise<void>;
  isSubmitting: boolean;
  projects: ProjectOption[];
  clients: ClientOption[];
  initial?: PackageFormState;
}

const PackageDialog: React.FC<PackageDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  projects,
  clients,
  initial,
}) => {
  const [form, setForm] = useState<PackageFormState>(initial ?? defaultPackageForm);

  useEffect(() => {
    setForm(initial ?? defaultPackageForm);
  }, [initial, open]);

  const handleTextChange =
    (field: keyof PackageFormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value ?? '';
      setForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleSelectChange =
    (field: keyof PackageFormState) =>
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value ?? '';
      setForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleSubmit = async () => {
    await onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Редактирование пакета' : 'Новый пакет лидов'}</DialogTitle>
      <DialogContent sx={{ pt: 2 }} data-testid="package-form">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ProjectAutocomplete
              projects={projects}
              value={form.projectId}
              onChange={(val) => setForm(prev => ({ ...prev, projectId: val }))}
              label="Основной проект"
              required
              size="medium"
            />
          </Grid>
          <Grid item xs={12}>
            <ProjectAutocomplete
              projects={projects.filter(p => String(p.id) !== String(form.projectId))}
              value={form.secondaryProjectIds}
              onChange={(vals) => setForm(prev => ({ ...prev, secondaryProjectIds: vals }))}
              label="Дополнительные проекты (общая квота)"
              size="medium"
              multiple
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="finance-package-client-label">Клиент</InputLabel>
              <Select
                labelId="finance-package-client-label"
                value={form.clientId}
                label="Клиент"
                onChange={handleSelectChange('clientId')}
              >
                <MenuItem value="">Не указан</MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={String(client.id)}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Название пакета"
              value={form.title}
              onChange={handleTextChange('title')}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Количество лидов"
              value={form.leadQuota}
              onChange={handleTextChange('leadQuota')}
              type="number"
              fullWidth
              required
              inputProps={{ min: 0, 'data-testid': 'package-leads-count' }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Стоимость пакета"
              value={form.totalAmount}
              onChange={handleTextChange('totalAmount')}
              type="number"
              fullWidth
              required
              inputProps={{
                min: 0,
                step: 0.01,
                'data-testid': 'package-total-sum',
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Стоимость лида"
              value={form.unitPrice}
              onChange={handleTextChange('unitPrice')}
              type="number"
              fullWidth
              inputProps={{
                min: 0,
                step: 0.01,
                'data-testid': 'package-price-per-lead',
              }}
              helperText="Можно оставить пустым для авторасчёта"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Оплачено"
              value={form.paidAmount}
              onChange={handleTextChange('paidAmount')}
              type="number"
              fullWidth
              inputProps={{ step: 0.01 }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel id="finance-package-status-label">Статус оплаты</InputLabel>
              <Select
                labelId="finance-package-status-label"
                value={form.paymentStatus}
                label="Статус оплаты"
                onChange={handleSelectChange('paymentStatus')}
                SelectDisplayProps={
                  { 'data-testid': 'package-status' } as React.HTMLAttributes<HTMLDivElement>
                }
              >
                {(Object.keys(PAYMENT_STATUS_LABELS) as LeadPackagePaymentStatus[]).map((status) => (
                  <MenuItem key={status} value={status}>
                    {PAYMENT_STATUS_LABELS[status]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Дата оплаты"
              type="date"
              value={form.paymentDate}
              onChange={handleTextChange('paymentDate')}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Номер счёта"
              value={form.invoiceNumber}
              onChange={handleTextChange('invoiceNumber')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Комментарий"
              value={form.notes}
              onChange={handleTextChange('notes')}
              fullWidth
              multiline
              minRows={2}
              inputProps={{ 'data-testid': 'package-notes' }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          variant="contained"
          data-testid="package-submit-btn"
        >
          {isSubmitting ? 'Сохранение...' : 'Сохранить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface AdjustmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: AdjustmentFormState) => Promise<void>;
  isSubmitting: boolean;
  projects: ProjectOption[];
  clients: ClientOption[];
}

const AdjustmentDialog: React.FC<AdjustmentDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  projects,
  clients,
}) => {
  const [form, setForm] = useState<AdjustmentFormState>(defaultAdjustmentForm);

  useEffect(() => {
    if (!open) {
      setForm(defaultAdjustmentForm);
    }
  }, [open]);

  const handleAdjustmentTextChange =
    (field: keyof AdjustmentFormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value ?? '';
      setForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleAdjustmentSelectChange =
    (field: keyof AdjustmentFormState) =>
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value ?? '';
      setForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleSubmit = async () => {
    await onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Корректировка баланса</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ProjectAutocomplete
              projects={projects}
              value={form.projectId}
              onChange={(val) => setForm(prev => ({ ...prev, projectId: val }))}
              label="Проект"
              required
              size="medium"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="finance-adjustment-client-label">Клиент</InputLabel>
              <Select
                labelId="finance-adjustment-client-label"
                value={form.clientId}
                label="Клиент"
                onChange={handleAdjustmentSelectChange('clientId')}
              >
                <MenuItem value="">Не указан</MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={String(client.id)}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Сумма"
              type="number"
              value={form.amount}
              onChange={handleAdjustmentTextChange('amount')}
              fullWidth
              required
              helperText="Положительная — пополнение, отрицательная — списание"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Количество лидов"
              type="number"
              value={form.leadCount}
              onChange={handleAdjustmentTextChange('leadCount')}
              fullWidth
              helperText="Опционально"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Дата"
              type="datetime-local"
              value={form.effectiveAt}
              onChange={handleAdjustmentTextChange('effectiveAt')}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Комментарий"
              value={form.description}
              onChange={handleAdjustmentTextChange('description')}
              fullWidth
              multiline
              minRows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {isSubmitting ? 'Сохранение...' : 'Добавить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface TimeseriesChartProps {
  data: FinanceTimeseriesPoint[];
}

const TimeseriesChart: React.FC<TimeseriesChartProps> = ({ data }) => {
  if (!data.length) {
    return (
      <Box
        sx={{
          height: 220,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography color="text.secondary">Нет данных для графика</Typography>
      </Box>
    );
  }

  const width = Math.max(data.length * 80, 360);
  const height = 260;
  const padding = 50;
  const amounts = data.flatMap((point) => [
    point.balanceAmount,
    point.creditsAmount,
    -point.consumedAmount,
  ]);
  const maxAmount = Math.max(...amounts, 1);
  const minAmount = Math.min(...amounts, 0);
  const range = maxAmount - minAmount || 1;

  const scaleX = (index: number) => {
    if (data.length === 1) {
      return width / 2;
    }
    return padding + ((width - padding * 2) * index) / (data.length - 1);
  };

  const scaleY = (value: number) =>
    height - padding - ((value - minAmount) / range) * (height - padding * 2);

  const balancePoints = data
    .map((point, index) => `${scaleX(index)},${scaleY(point.balanceAmount)}`)
    .join(' ');

  const creditsPoints = data
    .map((point, index) => `${scaleX(index)},${scaleY(point.creditsAmount)}`)
    .join(' ');

  const consumedPoints = data
    .map((point, index) => `${scaleX(index)},${scaleY(point.consumedAmount * -1)}`)
    .join(' ');

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg width={width} height={height}>
        <line
          x1={padding}
          y1={scaleY(0)}
          x2={width - padding}
          y2={scaleY(0)}
          stroke="#ccc"
          strokeDasharray="4 4"
        />
        <polyline
          points={balancePoints}
          fill="none"
          stroke="#1976d2"
          strokeWidth={3}
        />
        <polyline
          points={creditsPoints}
          fill="none"
          stroke="#2e7d32"
          strokeWidth={2}
          strokeDasharray="6 3"
        />
        <polyline
          points={consumedPoints}
          fill="none"
          stroke="#d32f2f"
          strokeWidth={2}
          strokeDasharray="2 2"
        />
        {data.map((point, index) => {
          const x = scaleX(index);
          const y = height - padding + 16;
          return (
            <text
              key={`label-${index}`}
              x={x}
              y={y}
              fontSize={12}
              textAnchor="middle"
              fill="#555"
            >
              {new Date(point.bucket).toLocaleDateString('ru-RU', {
                day: '2-digit',
                month: 'short',
              })}
            </text>
          );
        })}
        <text
          x={padding}
          y={padding - 20}
          fontSize={12}
          fill="#1976d2"
        >
          Баланс
        </text>
        <text
          x={padding + 120}
          y={padding - 20}
          fontSize={12}
          fill="#2e7d32"
        >
          Кредиты
        </text>
        <text
          x={padding + 220}
          y={padding - 20}
          fontSize={12}
          fill="#d32f2f"
        >
          Расход
        </text>
      </svg>
    </Box>
  );
};

const useDefaultDateRange = () => {
  const today = new Date();
  const fromDate = new Date(today);
  fromDate.setDate(today.getDate() - 30);
  return {
    from: fromDate.toISOString().slice(0, 10),
    to: today.toISOString().slice(0, 10),
  };
};

export const FinancePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isReadOnly = (() => { try { const t = localStorage.getItem('token'); if (!t) return true; const u = JSON.parse(atob(t.split('.')[1])); return u?.role === 'SALES'; } catch { return false; } })();
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);

  const defaultDates = useDefaultDateRange();
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>(defaultDates);

  const [packages, setPackages] = useState<LeadFinancePackage[]>([]);
  const [packagesTotal, setPackagesTotal] = useState(0);
  const [entries, setEntries] = useState<LeadFinanceEntry[]>([]);
  const [summary, setSummary] = useState<{ total: FinanceSummaryItem; byProject: FinanceSummaryItem[] } | null>(null);
  const [timeseries, setTimeseries] = useState<FinanceTimeseriesPoint[]>([]);

  const [loadingPackages, setLoadingPackages] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingTimeseries, setLoadingTimeseries] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<LeadFinancePackage | null>(null);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);

  const [hideProjectsWithoutActivePackages, setHideProjectsWithoutActivePackages] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');

  const showToast = useCallback(
    (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
      setToastMessage(message);
      setToastSeverity(severity);
      setToastOpen(true);
    },
    [],
  );

  const handleCloseToast = useCallback(() => {
    setToastOpen(false);
  }, []);

  const loadProjectsAndClients = useCallback(async () => {
    try {
      const [projectsResponse, clientsResponse] = await Promise.all([
        getProjects(),
        getClients(),
      ]);
      setProjects(projectsResponse);
      setClients(clientsResponse);
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось загрузить справочники');
    }
  }, []);

  const loadPackages = useCallback(async () => {
    setLoadingPackages(true);
    try {
      const response = await getFinancePackages({
        projectId: selectedProject || undefined,
        limit: 100,
      });
      setPackages(response.data);
      setPackagesTotal(response.total);
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось загрузить пакеты');
    } finally {
      setLoadingPackages(false);
    }
  }, [selectedProject]);

  const loadEntries = useCallback(async () => {
    setLoadingEntries(true);
    try {
      const response = await getFinanceEntries({
        projectId: selectedProject || undefined,
        limit: 100,
      });
      setEntries(response.data);
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось загрузить корректировки');
    } finally {
      setLoadingEntries(false);
    }
  }, [selectedProject]);

  const loadSummaryAndTimeseries = useCallback(async () => {
    setLoadingSummary(true);
    setLoadingTimeseries(true);
    try {
      const [summaryResponse, timeseriesResponse] = await Promise.all([
        getFinanceSummary({
          projectId: selectedProject || undefined,
          from: dateRange.from ? new Date(dateRange.from).toISOString() : undefined,
          to: dateRange.to ? new Date(dateRange.to + 'T23:59:59').toISOString() : undefined,
        }),
        getFinanceTimeseries({
          projectId: selectedProject || undefined,
          from: dateRange.from ? new Date(dateRange.from).toISOString() : undefined,
          to: dateRange.to ? new Date(dateRange.to + 'T23:59:59').toISOString() : undefined,
          groupBy: 'day',
        }),
      ]);
      setSummary(summaryResponse);
      setTimeseries(timeseriesResponse);
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось загрузить статистику');
    } finally {
      setLoadingSummary(false);
      setLoadingTimeseries(false);
    }
  }, [selectedProject, dateRange]);

  useEffect(() => {
    loadProjectsAndClients().catch(() => null);
  }, [loadProjectsAndClients]);

  useEffect(() => {
    loadPackages().catch(() => null);
    loadEntries().catch(() => null);
  }, [loadPackages, loadEntries]);

  useEffect(() => {
    loadSummaryAndTimeseries().catch(() => null);
  }, [loadSummaryAndTimeseries]);

  const handleOpenCreatePackage = () => {
    setEditingPackage(null);
    setPackageDialogOpen(true);
  };

  const handleOpenEditPackage = (pkg: LeadFinancePackage) => {
    setEditingPackage(pkg);
    setPackageDialogOpen(true);
  };

  const handleSubmitPackage = async (form: PackageFormState) => {
    const payload = {
      projectId: Number(form.projectId),
      secondaryProjectIds: form.secondaryProjectIds.map(id => Number(id)),
      clientId: form.clientId ? Number(form.clientId) : undefined,
      title: form.title || undefined,
      leadQuota: Number(form.leadQuota || 0),
      unitPrice: form.unitPrice ? Number(form.unitPrice) : undefined,
      totalAmount: Number(form.totalAmount || 0),
      paidAmount: form.paidAmount ? Number(form.paidAmount) : undefined,
      paymentStatus: form.paymentStatus,
      paymentDate: form.paymentDate || undefined,
      invoiceNumber: form.invoiceNumber || undefined,
      notes: form.notes || undefined,
    };

    setSubmitting(true);
    try {
      if (editingPackage) {
        await updateFinancePackage(editingPackage.id, payload);
      } else {
        await createFinancePackage(payload);
      }
      setPackageDialogOpen(false);
      await loadPackages();
      await loadSummaryAndTimeseries();
      showToast(
        editingPackage ? 'Пакет успешно обновлён' : 'Пакет успешно создан',
        'success',
      );
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось сохранить пакет');
      showToast(err?.message ?? 'Не удалось сохранить пакет', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePackage = async (pkg: LeadFinancePackage) => {
    if (
      window.confirm(
        `Отменить пакет "${pkg.title ?? `#${pkg.id}`}" и списать остаток?`,
      )
    ) {
      try {
        await deleteFinancePackage(pkg.id);
        await loadPackages();
        await loadSummaryAndTimeseries();
        showToast('Пакет отменён', 'success');
      } catch (err: any) {
        setError(err?.message ?? 'Не удалось отменить пакет');
        showToast(err?.message ?? 'Не удалось отменить пакет', 'error');
      }
    }
  };

  const handleSubmitAdjustment = async (form: AdjustmentFormState) => {
    const payload = {
      projectId: Number(form.projectId),
      clientId: form.clientId ? Number(form.clientId) : undefined,
      amount: Number(form.amount),
      leadCount: form.leadCount ? Number(form.leadCount) : undefined,
      description: form.description || undefined,
      effectiveAt: form.effectiveAt || undefined,
    };

    setSubmitting(true);
    try {
      await createFinanceAdjustment(payload);
      setAdjustmentDialogOpen(false);
      await loadEntries();
      await loadSummaryAndTimeseries();
      showToast('Корректировка добавлена', 'success');
    } catch (err: any) {
      setError(err?.message ?? 'Не удалось добавить корректировку');
      showToast(err?.message ?? 'Не удалось добавить корректировку', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const editingPackageForm = useMemo<PackageFormState | undefined>(() => {
    if (!editingPackage) return undefined;
    return {
      projectId: String(editingPackage.projectId),
      secondaryProjectIds: (editingPackage.projectIds || [])
        .filter(id => id !== editingPackage.projectId)
        .map(id => String(id)),
      clientId: editingPackage.clientId ? String(editingPackage.clientId) : '',
      title: editingPackage.title ?? '',
      leadQuota: String(editingPackage.leadQuota),
      unitPrice: editingPackage.unitPrice !== null ? String(editingPackage.unitPrice) : '',
      totalAmount: String(editingPackage.totalAmount),
      paidAmount: String(editingPackage.paidAmount ?? ''),
      paymentStatus: editingPackage.paymentStatus,
      paymentDate: toInputDate(editingPackage.paymentDate),
      invoiceNumber: editingPackage.invoiceNumber ?? '',
      notes: editingPackage.notes ?? '',
    };
  }, [editingPackage]);

  const balanceIsNegative = summary?.total?.balanceAmount !== undefined && summary.total.balanceAmount < 0;

  const filteredProjects = useMemo(() => {
    if (!summary?.byProject) return [];
    if (!hideProjectsWithoutActivePackages) return summary.byProject;
    return summary.byProject.filter((item) => item.balanceLeadCount > 0);
  }, [summary, hideProjectsWithoutActivePackages]);

  const handleDateChange =
    (field: 'from' | 'to') => (event: React.ChangeEvent<HTMLInputElement>) => {
      setDateRange((prev) => ({ ...prev, [field]: event.target.value }));
    };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        mb: 3,
        gap: 2
      }}>
        <Typography variant={isMobile ? "h5" : "h4"}>Финансы / Пакеты лидов</Typography>
        <Stack 
          direction={isMobile ? 'column' : 'row'} 
          spacing={2}
          sx={{ width: isMobile ? '100%' : 'auto' }}
        >
          <ProjectAutocomplete
            projects={projects}
            value={selectedProject}
            onChange={(val) => setSelectedProject(val)}
            sx={{ minWidth: isMobile ? '100%' : 220 }}
            size="small"
          />
          <Box sx={{ display: 'flex', gap: 2, width: isMobile ? '100%' : 'auto' }}>
            <TextField
              type="date"
              label="Дата с"
              size="small"
              value={dateRange.from}
              onChange={handleDateChange('from')}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              type="date"
              label="Дата по"
              size="small"
              value={dateRange.to}
              onChange={handleDateChange('to')}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Box>
          <Button 
            variant="outlined" 
            onClick={() => loadSummaryAndTimeseries()}
            fullWidth={isMobile}
          >
            Обновить статистику
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mb: 3 }}
      >
        <Tab label="Пакеты и оплаты" />
        <Tab label="Статистика по лидам" />
        <Tab label="Unit Economics" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          {!isReadOnly && (
          <Stack direction={isMobile ? 'column' : 'row'} spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              onClick={handleOpenCreatePackage}
              data-testid="add-package-btn"
              fullWidth={isMobile}
            >
              Новый пакет
            </Button>
            <Button
              variant="outlined"
              onClick={() => setAdjustmentDialogOpen(true)}
              fullWidth={isMobile}
            >
              Добавить корректировку
            </Button>
          </Stack>
          )}

          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6">Оплаченные пакеты</Typography>
              <Typography variant="body2" color="text.secondary">
                Всего записей: {numberFormatter.format(packagesTotal)}
              </Typography>
            </Box>
            {loadingPackages ? (
              <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : isMobile ? (
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {packages.map((pkg) => (
                  <Card key={pkg.id} variant="outlined">
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {pkg.title ?? `Пакет #${pkg.id}`}
                        </Typography>
                        <Chip
                          size="small"
                          color={
                            pkg.paymentStatus === 'PAID'
                              ? 'success'
                              : pkg.paymentStatus === 'PARTIALLY_PAID'
                              ? 'warning'
                              : pkg.paymentStatus === 'CANCELLED' || pkg.paymentStatus === 'REFUNDED'
                              ? 'default'
                              : 'info'
                          }
                          label={PAYMENT_STATUS_LABELS[pkg.paymentStatus]}
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Проекты: {pkg.projectNames?.length ? pkg.projectNames.join(', ') : pkg.projectName ?? '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Клиент: {pkg.clientName ?? '—'}
                      </Typography>

                      <Divider sx={{ my: 1 }} />

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                        <Box sx={{ flex: '1 1 40%' }}>
                          <Typography variant="caption" color="text.secondary">Лиды</Typography>
                          <Typography variant="body2">{numberFormatter.format(pkg.leadQuota)}</Typography>
                        </Box>
                        <Box sx={{ flex: '1 1 40%' }}>
                          <Typography variant="caption" color="text.secondary">Стоимость</Typography>
                          <Typography variant="body2">{currencyFormatter.format(pkg.totalAmount)}</Typography>
                        </Box>
                        <Box sx={{ flex: '1 1 40%' }}>
                          <Typography variant="caption" color="text.secondary">Оплачено</Typography>
                          <Typography variant="body2">{currencyFormatter.format(pkg.paidAmount ?? 0)}</Typography>
                        </Box>
                        <Box sx={{ flex: '1 1 40%' }}>
                          <Typography variant="caption" color="text.secondary">Цена лида</Typography>
                          <Typography variant="body2">
                            {pkg.unitPrice !== null ? currencyFormatter.format(pkg.unitPrice) : '—'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Оплата: {pkg.paymentDate ? new Date(pkg.paymentDate).toLocaleDateString('ru-RU') : '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Создан: {new Date(pkg.createdAt).toLocaleDateString('ru-RU')}
                          </Typography>
                        </Box>
                        {!isReadOnly && (
                        <Stack direction="row" spacing={1}>
                          <IconButton size="small" color="primary" onClick={() => handleOpenEditPackage(pkg)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeletePackage(pkg)}
                            disabled={pkg.paymentStatus === 'CANCELLED'}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                {!packages.length && (
                  <Typography align="center" color="text.secondary" sx={{ py: 2 }}>
                    Нет пакетов для отображения
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ p: 2, overflowX: 'auto' }}>
                <Table size="small" data-testid="packages-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Пакет</TableCell>
                      <TableCell>Проект</TableCell>
                      <TableCell>Клиент</TableCell>
                      <TableCell align="right">Лиды</TableCell>
                      <TableCell align="right">Стоимость</TableCell>
                      <TableCell align="right">Оплачено</TableCell>
                      <TableCell align="right">Цена лида</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Дата оплаты</TableCell>
                      <TableCell>Создано</TableCell>
                      {!isReadOnly && <TableCell align="right">Действия</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {packages.map((pkg) => (
                      <TableRow key={pkg.id} hover>
                        <TableCell>{pkg.title ?? `Пакет #${pkg.id}`}</TableCell>
                        <TableCell>{pkg.projectNames?.length ? pkg.projectNames.join(', ') : pkg.projectName ?? '—'}</TableCell>
                        <TableCell>{pkg.clientName ?? '—'}</TableCell>
                        <TableCell align="right">{numberFormatter.format(pkg.leadQuota)}</TableCell>
                        <TableCell align="right">{currencyFormatter.format(pkg.totalAmount)}</TableCell>
                        <TableCell align="right">{currencyFormatter.format(pkg.paidAmount ?? 0)}</TableCell>
                        <TableCell align="right">
                          {pkg.unitPrice !== null ? currencyFormatter.format(pkg.unitPrice) : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={
                              pkg.paymentStatus === 'PAID'
                                ? 'success'
                                : pkg.paymentStatus === 'PARTIALLY_PAID'
                                ? 'warning'
                                : pkg.paymentStatus === 'CANCELLED' || pkg.paymentStatus === 'REFUNDED'
                                ? 'default'
                                : 'info'
                            }
                            label={PAYMENT_STATUS_LABELS[pkg.paymentStatus]}
                          />
                        </TableCell>
                        <TableCell>
                          {pkg.paymentDate
                            ? new Date(pkg.paymentDate).toLocaleDateString('ru-RU')
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {new Date(pkg.createdAt).toLocaleDateString('ru-RU')}
                        </TableCell>
                        {!isReadOnly && (
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={() => handleOpenEditPackage(pkg)}>
                              Изменить
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleDeletePackage(pkg)}
                              disabled={pkg.paymentStatus === 'CANCELLED'}
                            >
                              Отменить
                            </Button>
                          </Stack>
                        </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {!packages.length && (
                      <TableRow>
                        <TableCell colSpan={11} align="center">
                          <Typography color="text.secondary">Нет пакетов для отображения</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>

          <Paper variant="outlined">
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6">Журнал корректировок</Typography>
            </Box>
            {loadingEntries ? (
              <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : isMobile ? (
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {entries.map((entry) => (
                  <Card key={entry.id} variant="outlined">
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {new Date(entry.effectiveAt).toLocaleString('ru-RU')}
                        </Typography>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ color: entry.amount < 0 ? 'error.main' : 'success.main', fontWeight: 'bold' }}
                        >
                          {currencyFormatter.format(entry.amount)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Проект: {entry.projectName ?? '—'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Клиент: {entry.clientName ?? '—'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {entry.description ?? '—'}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Лиды: {entry.leadCount ?? 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Автор: {entry.createdByName ?? '—'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                {!entries.length && (
                  <Typography align="center" color="text.secondary" sx={{ py: 2 }}>
                    Нет корректировок
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ p: 2, overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Дата</TableCell>
                      <TableCell>Проект</TableCell>
                      <TableCell>Клиент</TableCell>
                      <TableCell align="right">Сумма</TableCell>
                      <TableCell align="right">Лиды</TableCell>
                      <TableCell>Комментарий</TableCell>
                      <TableCell>Автор</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          {new Date(entry.effectiveAt).toLocaleString('ru-RU')}
                        </TableCell>
                        <TableCell>{entry.projectName ?? '—'}</TableCell>
                        <TableCell>{entry.clientName ?? '—'}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: entry.amount < 0 ? 'error.main' : 'success.main' }}
                        >
                          {currencyFormatter.format(entry.amount)}
                        </TableCell>
                        <TableCell align="right">{entry.leadCount ?? 0}</TableCell>
                        <TableCell>{entry.description ?? '—'}</TableCell>
                        <TableCell>{entry.createdByName ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                    {!entries.length && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary">Нет корректировок</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Paper elevation={balanceIsNegative ? 4 : 1} sx={{ p: 2, borderLeft: balanceIsNegative ? '4px solid #d32f2f' : undefined }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Баланс (руб.)
                </Typography>
                {loadingSummary ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant={isMobile ? "h6" : "h5"} color={balanceIsNegative ? 'error.main' : 'success.main'}>
                    {summary ? currencyFormatter.format(summary.total.balanceAmount) : '—'}
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Баланс (лиды)
                </Typography>
                {loadingSummary ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant={isMobile ? "h6" : "h5"} color={summary && summary.total.balanceLeadCount < 0 ? 'error.main' : 'text.primary'}>
                    {summary ? numberFormatter.format(summary.total.balanceLeadCount) : '—'}
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Подтверждённых лидов
                </Typography>
                {loadingSummary ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant={isMobile ? "h6" : "h5"}>
                    {summary ? numberFormatter.format(summary.total.approvedLeadCount) : '—'}
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid', 
              borderColor: 'divider', 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between', 
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: 1
            }}>
              <Typography variant="h6">Текущее состояние проектов</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={hideProjectsWithoutActivePackages}
                    onChange={(e) => setHideProjectsWithoutActivePackages(e.target.checked)}
                    size="small"
                  />
                }
                label={isMobile ? "Активные пакеты" : "Скрыть проекты без активных пакетов"}
                sx={{ ml: 0 }}
              />
            </Box>
            {loadingSummary ? (
              <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : isMobile ? (
              <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredProjects.map((item) => (
                  <Card key={item.projectId ?? 'total'} variant="outlined">
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {item.projectName ?? '—'}
                      </Typography>
                      
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Кредиты (руб.)</Typography>
                          <Typography variant="body2">{currencyFormatter.format(item.creditsAmount)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Кредиты (лиды)</Typography>
                          <Typography variant="body2">{numberFormatter.format(item.entriesLeadCount)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Расход (руб.)</Typography>
                          <Typography variant="body2">{currencyFormatter.format(item.approvedAmount)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Расход (лиды)</Typography>
                          <Typography variant="body2">{numberFormatter.format(item.approvedLeadCount)}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Сальдо (руб.)</Typography>
                          <Typography variant="body2" sx={{ color: item.balanceAmount < 0 ? 'error.main' : 'success.main', fontWeight: 'bold' }}>
                            {currencyFormatter.format(item.balanceAmount)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Сальдо (лиды)</Typography>
                          <Typography variant="body2" sx={{ color: item.balanceLeadCount < 0 ? 'error.main' : 'text.primary', fontWeight: 'bold' }}>
                            {numberFormatter.format(item.balanceLeadCount)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
                {!filteredProjects.length && (
                  <Typography align="center" color="text.secondary" sx={{ py: 2 }}>
                    Нет данных
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ p: 2, overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Проект</TableCell>
                      <TableCell align="right">Кредиты (руб.)</TableCell>
                      <TableCell align="right">Кредиты (лиды)</TableCell>
                      <TableCell align="right">Расход (руб.)</TableCell>
                      <TableCell align="right">Расход (лиды)</TableCell>
                      <TableCell align="right">Сальдо (руб.)</TableCell>
                      <TableCell align="right">Сальдо (лиды)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProjects.map((item) => (
                      <TableRow key={item.projectId ?? 'total'}>
                        <TableCell>{item.projectName ?? '—'}</TableCell>
                        <TableCell align="right">{currencyFormatter.format(item.creditsAmount)}</TableCell>
                        <TableCell align="right">{numberFormatter.format(item.entriesLeadCount)}</TableCell>
                        <TableCell align="right">{currencyFormatter.format(item.approvedAmount)}</TableCell>
                        <TableCell align="right">{numberFormatter.format(item.approvedLeadCount)}</TableCell>
                        <TableCell align="right" sx={{ color: item.balanceAmount < 0 ? 'error.main' : 'success.main' }}>
                          {currencyFormatter.format(item.balanceAmount)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: item.balanceLeadCount < 0 ? 'error.main' : 'text.primary' }}>
                          {numberFormatter.format(item.balanceLeadCount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!filteredProjects.length && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary">Нет данных по выбранным фильтрам</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>

          <Paper variant="outlined">
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6">Динамика</Typography>
            </Box>
            {loadingTimeseries ? (
              <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                <TimeseriesChart data={timeseries} />
                {isMobile ? (
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {timeseries.map((point) => (
                      <Card key={`${point.bucket}-${point.projectId ?? 'all'}`} variant="outlined">
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {new Date(point.bucket).toLocaleDateString('ru-RU')}
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Кредиты (руб.)</Typography>
                              <Typography variant="body2">{currencyFormatter.format(point.creditsAmount)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Расход (руб.)</Typography>
                              <Typography variant="body2">{currencyFormatter.format(point.consumedAmount)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Баланс (руб.)</Typography>
                              <Typography variant="body2" sx={{ color: point.balanceAmount < 0 ? 'error.main' : 'success.main' }}>
                                {currencyFormatter.format(point.balanceAmount)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Кредиты (лиды)</Typography>
                              <Typography variant="body2">{numberFormatter.format(point.creditsLeadCount)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Расход (лиды)</Typography>
                              <Typography variant="body2">{numberFormatter.format(point.consumedLeadCount)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Баланс (лиды)</Typography>
                              <Typography variant="body2" sx={{ color: point.balanceLeadCount < 0 ? 'error.main' : 'text.primary' }}>
                                {numberFormatter.format(point.balanceLeadCount)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))}
                    {!timeseries.length && (
                      <Typography align="center" color="text.secondary" sx={{ py: 2 }}>
                        Нет данных
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ mt: 2, overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Дата</TableCell>
                          <TableCell align="right">Кредиты (руб.)</TableCell>
                          <TableCell align="right">Расход (руб.)</TableCell>
                          <TableCell align="right">Баланс (руб.)</TableCell>
                          <TableCell align="right">Кредиты (лиды)</TableCell>
                          <TableCell align="right">Расход (лиды)</TableCell>
                          <TableCell align="right">Баланс (лиды)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {timeseries.map((point) => (
                          <TableRow key={`${point.bucket}-${point.projectId ?? 'all'}`}>
                            <TableCell>
                              {new Date(point.bucket).toLocaleDateString('ru-RU')}
                            </TableCell>
                            <TableCell align="right">{currencyFormatter.format(point.creditsAmount)}</TableCell>
                            <TableCell align="right">{currencyFormatter.format(point.consumedAmount)}</TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: point.balanceAmount < 0 ? 'error.main' : 'success.main' }}
                            >
                              {currencyFormatter.format(point.balanceAmount)}
                            </TableCell>
                            <TableCell align="right">{numberFormatter.format(point.creditsLeadCount)}</TableCell>
                            <TableCell align="right">{numberFormatter.format(point.consumedLeadCount)}</TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: point.balanceLeadCount < 0 ? 'error.main' : 'text.primary' }}
                            >
                              {numberFormatter.format(point.balanceLeadCount)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {!timeseries.length && (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <Typography color="text.secondary">Нет данных</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {activeTab === 2 && (
        <ProjectExpensesTab projectId={Number(selectedProject)} />
      )}

      <PackageDialog
        open={packageDialogOpen}
        onClose={() => setPackageDialogOpen(false)}
        onSubmit={handleSubmitPackage}
        isSubmitting={submitting}
        projects={projects}
        clients={clients}
        initial={editingPackageForm}
      />

      <AdjustmentDialog
        open={adjustmentDialogOpen}
        onClose={() => setAdjustmentDialogOpen(false)}
        onSubmit={handleSubmitAdjustment}
        isSubmitting={submitting}
        projects={projects}
        clients={clients}
      />

      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toastSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

